<template>
  <div>
    <BaseDialog
      ref="dialog"
      contentClass="platform-editor"
      sidePanel
      persistent
      show-actions
      :on-close="requestClose"
      @closeModal="requestClose"
    >
      <template slot="title">
        <div v-if="draft" class="platform-editor__heading">
          <PlatformIcon :platform="draft.id" />
          <span>{{ $t('page.admin-platform-catalog.editor.title', { name: originalName }) }}</span>
          <code class="platform-editor__heading-id">{{ draft.id }}</code>
        </div>
      </template>

      <template slot="content">
        <div v-if="draft" class="platform-editor">
          <form class="platform-editor__form" @submit.prevent>
            <fieldset class="platform-editor__fieldset" :disabled="saving">
              <BaseInfoBanner
                class="platform-editor__banner"
                tone="info"
                preset="soft"
                :title="$t('page.admin-platform-catalog.editor.shared-notice')"
              >
                <template #icon>
                  <BaseIcon name="info" />
                </template>
              </BaseInfoBanner>

              <BaseInfoBanner
                v-if="conflict"
                class="platform-editor__banner"
                tone="warning"
                preset="soft"
                :title="$t('page.admin-platform-catalog.editor.conflict-title')"
                :description="$t('page.admin-platform-catalog.editor.conflict-description')"
              >
                <template #icon>
                  <BaseIcon name="alert-triangle" />
                </template>
                <template #action>
                  <BaseButton is-small is-outlined @click.native="reloadFromServer">
                    {{ $t('page.admin-platform-catalog.editor.conflict-reload') }}
                  </BaseButton>
                </template>
              </BaseInfoBanner>

              <section class="platform-editor__section">
                <h3 class="platform-editor__section-title">
                  {{ $t('page.admin-platform-catalog.sections.identity') }}
                </h3>
                <div class="platform-editor__grid">
                  <FormInput
                    has-gray-background
                    name="platform-id"
                    :label="$t('page.admin-platform-catalog.fields.id')"
                    :value="draft.id"
                    :hint="$t('page.admin-platform-catalog.editor.id-hint')"
                    readonly
                  />
                  <FormInput
                    has-gray-background
                    name="platform-url"
                    :label="$t('page.admin-platform-catalog.fields.url')"
                    v-model="draft.url"
                    placeholder="https://"
                    :error="fieldError('url')"
                  />
                  <FormInput
                    has-gray-background
                    name="platform-name-fr"
                    :label="$t('page.admin-platform-catalog.fields.name-fr')"
                    v-model="draft.nameFr"
                    :error="fieldError('nameFr')"
                    is-required
                  />
                  <FormInput
                    has-gray-background
                    name="platform-name-en"
                    :label="$t('page.admin-platform-catalog.fields.name-en')"
                    v-model="draft.nameEn"
                    :error="fieldError('nameEn')"
                    is-required
                  />
                </div>
              </section>

              <section class="platform-editor__section">
                <h3 class="platform-editor__section-title">{{ $t('page.admin-platform-catalog.sections.tags') }}</h3>
                <div class="platform-editor__tags">
                  <FormCheckbox
                    v-for="tag in tagOptions"
                    :key="tag"
                    :label="$t('tags.' + tag)"
                    :sublabel="tagEffect(tag)"
                    :value="draft.tags.includes(tag)"
                    @input="toggleTag(tag, $event)"
                  />
                </div>
              </section>

              <section class="platform-editor__section">
                <h3 class="platform-editor__section-title">
                  {{ $t('page.admin-platform-catalog.sections.description') }}
                </h3>
                <div class="platform-editor__grid">
                  <FormInput
                    has-gray-background
                    name="platform-description-fr"
                    :label="$t('page.admin-platform-catalog.fields.description-fr')"
                    v-model="draft.descriptionFr"
                    is-textarea
                    rows="6"
                  />
                  <FormInput
                    has-gray-background
                    name="platform-description-en"
                    :label="$t('page.admin-platform-catalog.fields.description-en')"
                    v-model="draft.descriptionEn"
                    is-textarea
                    rows="6"
                  />
                </div>
              </section>

              <section class="platform-editor__section">
                <h3 class="platform-editor__section-title">
                  {{ $t('page.admin-platform-catalog.sections.regions') }}
                </h3>
                <PlatformRegionsPicker :value="draft.regions" @change="draft.regions = $event" />
                <BaseInfoBanner
                  class="platform-editor__regions-notice"
                  tone="info"
                  preset="soft"
                  :title="$t('page.admin-platform-catalog.regions-hint.platform-title')"
                  :description="$t('page.admin-platform-catalog.regions-hint.platform-description')"
                >
                  <template #icon>
                    <BaseIcon name="info" />
                  </template>
                </BaseInfoBanner>
              </section>

              <section class="platform-editor__section">
                <h3 class="platform-editor__section-title">
                  {{ $t('page.admin-platform-catalog.sections.sub-platforms', { count: draft.subPlatforms.length }) }}
                </h3>
                <SubPlatformsEditor
                  ref="subPlatforms"
                  v-model="draft.subPlatforms"
                  :field-errors="fieldErrors"
                  :show-errors="showErrors"
                />
              </section>
            </fieldset>
          </form>

          <aside class="platform-editor__preview">
            <div class="platform-editor__preview-head">
              <h3 class="platform-editor__section-title">{{ $t('page.admin-platform-catalog.preview.title') }}</h3>
              <BasePillGroup is-small :options="localeOptions" v-model="previewLocale" />
            </div>
            <PlatformCard :key="previewLocale" :platform="preview" :locale="previewLocale" />
            <p class="platform-editor__preview-note">{{ previewNote }}</p>
          </aside>
        </div>
      </template>

      <template slot="actions">
        <div class="platform-editor__actions">
          <span v-if="isDirty" class="platform-editor__unsaved">{{ $t('common.unsaved') }}</span>
          <BaseButton is-outlined @click.native="requestClose">{{ $t('actions.cancel') }}</BaseButton>
          <BaseButton :disabled="!isDirty || saving" :is-loading="saving" @click.native="save">
            {{ $t('actions.save') }}
          </BaseButton>
        </div>
      </template>
    </BaseDialog>

    <ConfirmationDialog
      ref="discardDialog"
      is-danger
      :buttonText="$t('page.admin-platform-catalog.editor.discard')"
      @execute="close"
    >
      <template slot="content">
        {{ $t('page.admin-platform-catalog.editor.discard-content') }}
      </template>
    </ConfirmationDialog>
  </div>
</template>

<script>
import BaseButton from '@/components/base/BaseButton';
import BaseDialog from '@/components/base/BaseDialog';
import BaseIcon from '@/components/base/BaseIcon';
import BaseInfoBanner from '@/components/base/BaseInfoBanner';
import BasePillGroup from '@/components/base/BasePillGroup';
import FormCheckbox from '@/components/form/FormCheckbox';
import FormInput from '@/components/form/FormInput';
import ConfirmationDialog from '@/components/page-admin-invites/ConfirmationDialog';
import PlatformRegionsPicker from '@/components/page-admin-platform-catalog/PlatformRegionsPicker';
import SubPlatformsEditor from '@/components/page-admin-platform-catalog/SubPlatformsEditor';
import PlatformCard from '@/components/PlatformCard';
import PlatformIcon from '@/components/PlatformIcon';
import { isSamePlatform, toPlatformPayload, validatePlatform } from '@/models/platform-catalog';
import { platformsService } from '@/services/platforms.service';
import { LOCALE } from '@/util/i18n/locale';
import { PLATFORM_TAGS } from '@/util/tag-tones/platform-tags';
import { mapActions } from 'vuex';

// Tags whose effect isn't just the badge; the others get no explanation.
const TAG_EFFECTS = ['old', 'maintenance'];

let nextKey = 0;

const toDraft = (platform) => {
  const payload = toPlatformPayload(platform);
  return {
    ...payload,
    descriptionFr: payload.descriptionFr || '',
    descriptionEn: payload.descriptionEn || '',
    url: payload.url || '',
    subPlatforms: payload.subPlatforms.map((sub) => ({
      ...sub,
      _key: `sub-${nextKey++}`,
      descriptionFr: sub.descriptionFr || '',
      descriptionEn: sub.descriptionEn || '',
      url: sub.url || '',
    })),
  };
};

/**
 * Side panel editing one catalog platform, with a live preview of the card users see in the
 * platforms detail panel. The id is read-only: tenders, scrapers and tenant access all key on it.
 * Save sends the whole platform (the API's PUT is a full replace), after checking nobody saved it
 * since the panel opened, so one admin can't silently overwrite another's edit.
 */
export default {
  name: 'PlatformEditorDialog',
  components: {
    BaseButton,
    BaseDialog,
    BaseIcon,
    BaseInfoBanner,
    BasePillGroup,
    ConfirmationDialog,
    FormCheckbox,
    FormInput,
    PlatformCard,
    PlatformIcon,
    PlatformRegionsPicker,
    SubPlatformsEditor,
  },
  data() {
    return {
      original: null,
      draft: null,
      showErrors: false,
      saving: false,
      conflict: null,
      previewLocale: this.$i18n.locale,
    };
  },
  computed: {
    originalName() {
      if (!this.original) return '';
      return this.$i18n.locale === LOCALE.FR_CA ? this.original.nameFr : this.original.nameEn;
    },
    tagOptions() {
      const unknown = this.original.tags.filter((tag) => !PLATFORM_TAGS.includes(tag));
      return [...PLATFORM_TAGS, ...unknown];
    },
    localeOptions() {
      return [
        { value: LOCALE.FR_CA, label: 'FR' },
        { value: LOCALE.EN_CA, label: 'EN' },
      ];
    },
    preview() {
      return toPlatformPayload(this.draft);
    },
    previewNote() {
      const isObsolete = this.draft.tags.includes('old');
      return this.$t(`page.admin-platform-catalog.preview.${isObsolete ? 'note-old' : 'note'}`);
    },
    fieldErrors() {
      return this.draft ? validatePlatform(this.draft) : {};
    },
    hasErrors() {
      return Object.keys(this.fieldErrors).length > 0;
    },
    isDirty() {
      return !!this.draft && !isSamePlatform(this.draft, this.original);
    },
  },
  methods: {
    ...mapActions('platforms', ['updatePlatform']),
    openDialog(platform) {
      this.reset(platform);
      this.previewLocale = this.$i18n.locale;
      this.$refs.dialog.openModal();
    },
    reset(platform) {
      this.original = toPlatformPayload(platform);
      this.draft = toDraft(platform);
      this.showErrors = false;
      this.conflict = null;
    },
    close() {
      this.$refs.dialog.isModalOpen = false;
    },
    requestClose() {
      if (this.saving) return;
      if (this.isDirty) {
        this.$refs.discardDialog.openDialog();
      } else {
        this.close();
      }
    },
    tagEffect(tag) {
      return TAG_EFFECTS.includes(tag) ? this.$t(`page.admin-platform-catalog.tag-effects.${tag}`) : null;
    },
    toggleTag(tag, checked) {
      const tags = new Set(this.draft.tags);
      if (checked) tags.add(tag);
      else tags.delete(tag);
      this.draft.tags = this.tagOptions.filter((option) => tags.has(option));
    },
    fieldError(path) {
      const error = this.showErrors && this.fieldErrors[path];
      return error ? this.$t(`page.admin-platform-catalog.errors.${error}`) : null;
    },
    reloadFromServer() {
      this.reset(this.conflict);
      this.$emit('refresh');
    },
    async save() {
      if (this.hasErrors) {
        this.showErrors = true;
        this.$refs.subPlatforms.openRowsWithErrors();
        this.$toast.error(this.$t('page.admin-platform-catalog.editor.invalid'));
        return;
      }
      // Taken now, before any await: what gets saved is exactly what was just validated.
      const payload = toPlatformPayload(this.draft);
      this.saving = true;
      try {
        const current = await platformsService.getPlatform(this.original.id);
        if (!isSamePlatform(current, this.original)) {
          this.conflict = current;
          this.$toast.error(this.$t('page.admin-platform-catalog.editor.conflict-title'));
          return;
        }
        await this.updatePlatform(payload);
        this.$toast.success(this.$t('toast.success'));
        this.close();
      } catch (error) {
        this.$toast.error(this.$t('error.unknown-error'));
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style lang="scss">
.base-dialog__platform-editor {
  width: min(1180px, 100vw);
  max-width: 100%;

  .base-dialog__header + .base-dialog__content {
    background-color: $color-white;
    padding: 0;
  }

  .base-dialog__actions {
    border-top: 1px solid $color-border;
    padding: 16px 24px;
  }
}

// Form and preview scroll separately, so the preview stays in view while the form scrolls.
// Stacked (narrow panel), the dialog content scrolls as a whole again.
.platform-editor {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 360px;
  flex: 1 1 auto;
  min-height: 0;

  @media (max-width: 1000px) {
    grid-template-columns: 1fr;
    flex: none;
  }

  &__heading {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
  }

  &__heading-id {
    padding: 2px 6px;
    border-radius: $radius-sm;
    background: $color-slate-50;
    color: $color-slate-500;
    font-size: 12px;
    font-weight: normal;
  }

  &__form {
    padding: 20px 24px 32px;
    overflow-y: auto;

    @media (max-width: 1000px) {
      overflow-y: visible;
    }
  }

  &__fieldset {
    min-width: 0;
    margin: 0;
    padding: 0;
    border: 0;
  }

  &__banner {
    margin-bottom: 16px;
  }

  &__regions-notice {
    margin-top: 10px;
  }

  &__section {
    & + & {
      margin-top: 24px;
    }
  }

  &__section-title {
    margin: 0 0 4px;
    color: $color-normal-pale;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.05em;
    text-transform: uppercase;
  }

  &__grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    column-gap: 16px;

    @media (max-width: 720px) {
      grid-template-columns: 1fr;
    }
  }

  &__tags {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    align-items: start;
    gap: 4px 16px;

    .form-checkbox {
      padding-left: 0;
    }
  }

  &__preview {
    padding: 0 24px 20px;
    overflow-y: auto;
    border-left: 1px solid $color-border;
    background: $color-neutral-pale;

    @media (max-width: 1000px) {
      order: -1;
      overflow-y: visible;
      border-left: none;
      border-bottom: 1px solid $color-border;
    }
  }

  // Pinned so the locale toggle stays reachable when an expanded sub-platform list makes the
  // preview scroll.
  &__preview-head {
    position: sticky;
    top: 0;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 20px 0 12px;
    background: $color-neutral-pale;

    .platform-editor__section-title {
      margin: 0;
    }
  }

  &__preview-note {
    margin: 12px 0 0;
    font-size: 12px;
    line-height: 1.4;
    color: $color-slate-500;
  }

  &__actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 12px;
  }

  &__unsaved {
    margin-right: auto;
    font-size: 13px;
    color: $color-slate-500;
  }
}
</style>
